import { SortingState, ColumnFiltersState } from "@tanstack/react-table";
import { intersection, isPlainObject, orderBy, sortBy, uniq } from "lodash";
import { parseISO } from "date-fns";
import { rankItem } from "@tanstack/match-sorter-utils";

import {
  issues,
  MonitoringIssue,
  me,
  IssueStatus,
  allPeople,
} from "../fixtures";

export type { MonitoringIssue } from "../fixtures";

const wait = async (timeout = 350) => {
  await new Promise((resolve) => {
    setTimeout(resolve, timeout);
  });
};

const _getIssuesById =
  (getBaseIssues: () => MonitoringIssue[]) => async (ids: string[]) => {
    await wait();

    const baseIssues = getBaseIssues();

    return { issues: baseIssues.filter((i) => ids.includes(i.id)) };
  };

const _getIssues =
  (getBaseIssues: () => MonitoringIssue[]) =>
  async (
    {
      pageIndex,
      pageSize,
    }: {
      pageIndex: number;
      pageSize: number;
    },
    sorting: SortingState,
    filtering: ColumnFiltersState,
    globalFilter: string,
  ) => {
    await wait();

    const baseIssues = getBaseIssues().map((issue) => {
      return {
        ...issue,
        sortableAlerts: [
          ...new Set(
            issue.alerts
              .map((alert) => alert.type)
              .flat()
              .sort(),
          ),
        ].join(", "),
      };
    });

    const sortFields: string[] = [];
    const sortOrder: ("asc" | "desc")[] = [];

    for (const sort of sorting) {
      sortFields.push(sort.id === "alerts" ? "sortableAlerts" : sort.id);
      sortOrder.push(sort.desc ? "desc" : "asc");
    }

    const sorted =
      sorting.length === 0
        ? baseIssues
        : orderBy(baseIssues, sortFields, sortOrder);

    let filtered = sorted;

    if (globalFilter) {
      filtered = filtered.filter((item) => {
        const result = rankItem(item, globalFilter, {
          accessors: [
            (v) => v.domain,
            (v) => v.status,
            (v) => v.date,
            (v) =>
              v.alerts
                .map((a) => a.type)
                .flat()
                .join(" "),
            (v) =>
              !v.assignee?.email ? "unassigned" : `${v.assignee?.email || ""}`,
          ],
        });
        return result.passed;
      });
    }

    for (const filter of filtering) {
      const { id, value } = filter;
      filtered = filtered.filter((v) => {
        if (id in v) {
          let rowValue: any = v[id as keyof typeof v];
          if (Array.isArray(rowValue) && id === "alerts") {
            rowValue = [...new Set(rowValue.map((v) => v.type).flat())].sort();
          }

          if (id === "assignee") {
            rowValue = (rowValue as any)?.email || "unassigned";
          }

          if (Array.isArray(value)) {
            if (Array.isArray(rowValue)) {
              return intersection(value, rowValue).length > 0;
            } else {
              return value.includes(rowValue);
            }
          } else if (
            isPlainObject(value) &&
            "comparator" in (value as any) &&
            "cutoffDate" in (value as any)
          ) {
            const comparator = (value as any).comparator as unknown as
              | "gte"
              | "lte";
            const cutoffDate = (value as any).cutoffDate as unknown as Date;

            const parsed = parseISO(rowValue as string);

            try {
              switch (comparator) {
                case "gte":
                  return parsed >= cutoffDate;
                case "lte":
                  return parsed <= cutoffDate;
                default:
                  return false;
              }
            } catch (e) {
              return false;
            }
          }
        }
      });
    }

    const processed = filtered;

    const start = pageIndex * pageSize;
    const end = start + pageSize;

    return {
      issues: processed.slice(start, end) as MonitoringIssue[],
      total: processed.length,
      totalPages: Math.ceil(processed.length / pageSize),
    };
  };

const _getIssuesAllValues =
  (getBaseIssues: () => MonitoringIssue[]) => async () => {
    await wait();

    const baseIssues = getBaseIssues();

    return {
      domain: sortBy(uniq(baseIssues.map((i) => i.domain).filter((x) => !!x))),
      status: sortBy(uniq(baseIssues.map((i) => i.status).filter((x) => !!x))),
      alerts: sortBy(
        uniq(
          baseIssues
            .flatMap((i) =>
              i.alerts.map((alert) => {
                return alert.type;
              }),
            )
            .flat()
            .sort()
            .filter((x) => !!x),
        ),
      ),
      assignee: [
        "unassigned",
        ...sortBy(
          uniq(baseIssues.map((i) => i.assignee?.email || "unassigned")),
        ),
      ],
    };
  };

export const getMyIssues = _getIssues(() =>
  issues.filter((i) => i.assignee === me),
);
export const getMyIssuesById = _getIssuesById(() =>
  issues.filter((i) => i.assignee === me),
);
export const getMyIssuesAllValues = _getIssuesAllValues(() =>
  issues.filter((i) => i.assignee === me),
);

export const getAllIssues = _getIssues(() => issues);
export const getAllIssuesById = _getIssuesById(() => issues);
export const getAllIssuesAllValues = _getIssuesAllValues(() => issues);

export const getOpenIssues = _getIssues(() =>
  issues.filter(
    (i) => ![IssueStatus.Resolved, IssueStatus.Muted].includes(i.status),
  ),
);
export const getOpenIssuesById = _getIssuesById(() =>
  issues.filter(
    (i) => ![IssueStatus.Resolved, IssueStatus.Muted].includes(i.status),
  ),
);
export const getOpenIssuesAllValues = _getIssuesAllValues(() =>
  issues.filter(
    (i) => ![IssueStatus.Resolved, IssueStatus.Muted].includes(i.status),
  ),
);

export const getMutedIssues = _getIssues(() =>
  issues.filter((i) => i.status === IssueStatus.Muted),
);
export const getMutedIssuesById = _getIssuesById(() =>
  issues.filter((i) => i.status === IssueStatus.Muted),
);
export const getMutedIssuesAllValues = _getIssuesAllValues(() =>
  issues.filter((i) => i.status === IssueStatus.Muted),
);

export const getResolvedIssues = _getIssues(() =>
  issues.filter((i) => i.status === IssueStatus.Resolved),
);
export const getResolvedIssuesById = _getIssuesById(() =>
  issues.filter((i) => i.status === IssueStatus.Resolved),
);
export const getResolvedIssuesAllValues = _getIssuesAllValues(() =>
  issues.filter((i) => i.status === IssueStatus.Resolved),
);

export const getAllPeople = async () => {
  await wait();
  return {
    people: allPeople,
  };
};
